// @ts-strict-ignore
import { LMContextService } from './lm-context.service';
import { FormResource, KeyedResource, Resource } from '@resources/index';
import { LMUrlUtilService } from './utils';
import { isEmpty } from 'lodash-es';
import { isEqual } from 'lodash-es';

import { BIDrillResource } from '@resources/bidrill.resource';
import { AppConstants } from '@utils/app.constants';
import { LMLoggerService } from '@services/lm-logger.service';
import { Injectable } from '@angular/core';
import { LMBreadcrumbService } from '@services/lm-breadcrumb.service';

@Injectable()
export class LMUrlInfoBuilderService {
  private static ServerLogVersionId = 'LMCLIENT UI ALERT: 2025.04';
  private static PrefixString = 'SPECURL DIFF';

  constructor(
    private lmBreadcrumbService: LMBreadcrumbService,
    private lmContextService: LMContextService,
    private lmLoggerService: LMLoggerService,
    private lmUrlUtilService: LMUrlUtilService,
  ) {}

  public getPageUrlInfoFromHttpUrl(httpUrl: string): LMPageUrlInfo {
    return this.getBaseUrlInfoFromHttpUrl(httpUrl) as LMPageUrlInfo;
  }

  public getListUrlInfoFromHttpUrl(httpUrl: string): LMListUrlInfo {
    return this.getBaseUrlInfoFromHttpUrl(httpUrl) as LMFormUrlInfo;
  }

  public getListUrlInfoFromListSpec(listSpec: LMListSpec, options?: LMListUrlInfoOptions): LMListUrlInfo {
    return this.getBaseUrlInfoFromListSpec(listSpec, options) as LMListUrlInfo;
  }

  public getFormUrlInfoFromHttpUrl(httpUrl: string): LMFormUrlInfo {
    return this.getBaseUrlInfoFromHttpUrl(httpUrl) as LMFormUrlInfo;
  }

  public getFormUrlInfoFromFormSpec(formSpec: LMFormSpec, options?: LMFormUrlInfoOptions): LMFormUrlInfo {
    return this.getBaseUrlInfoFromFormSpec(formSpec, options) as LMFormUrlInfo;
  }

  public getBaseUrlInfoFromHttpUrl(httpUrl: string, urlInfoOptions?: LMUrlInfoOptions): LMBaseUrlInfo {
    const basePath = this.buildBasePathFromHref(httpUrl);
    const urlParams= this.getUrlParams(httpUrl);
    const isGenUrl = this.lmUrlUtilService.isGenUrlUsingHref(httpUrl); // urlParams['csk.gen'] === 'true';
    const urlPath = this.lmUrlUtilService.getUrlPath(httpUrl);
    const resource = this.lmUrlUtilService.getResource(urlPath, isGenUrl);
    const sourceResource = this.getSourceResource(urlParams);
    const listResource = this.getResourceFromParams(urlParams, 'list');

    const filterId = this.lmUrlUtilService.getUrlParam(httpUrl, 'filterId');
    const action = this.lmUrlUtilService.getUrlParam(httpUrl, 'action') || undefined;
    const navigationContext = this.lmUrlUtilService.getPrefixedParamsFromParams(urlParams, 'nc.', true);
    const fromInbox = this.lmUrlUtilService.getUrlParam(httpUrl, 'fromInbox') === 'true';
    const processFlow = (navigationContext && Object.keys(navigationContext).length > 0
      && (navigationContext.hasOwnProperty('nc.PfiActivity') || navigationContext.hasOwnProperty('nc.ActionRequest')));
    const calledFrom = 'getBaseUrlInfoFromHttpUrl';
    const postRegistration = urlParams['postReg'];
    const templateTargetBusClass = urlParams['templateTargetBusClass'];
    const wfmDrillback = urlParams['WFMDRILLBACK'];

    let fromListResource: KeyedResource;
    if (sourceResource) {
      fromListResource = this.getResourceFromParams(urlParams, 'fromlist');
    }

    const urlInfo: LMBaseUrlInfo = {
      httpUrl,
      basePath,
      urlParams,
      isGenUrl,
      resource,
      sourceResource,
      listResource,
      fromListResource,
      action,
      navigationContext,
      fromInbox,
      processFlow,
      filterId,
      calledFrom,
      postRegistration,
      templateTargetBusClass,
      wfmDrillback
      // stacktrace: new Error().stack
    };

    if (this.lmContextService.useNewSpecUrls()) {
      if (urlInfo.urlParams[AppConstants.RESOURCETYPE_NAVIGATION] && urlInfo.urlParams['navToCube'] === 'true') {
        if (!urlInfoOptions) {
          urlInfoOptions = {};
        }
        urlInfoOptions.includeNavResourceIsSpecUrl = true;
      }
      urlInfo.specUrl = this.getSpecUrl(urlInfo, urlInfoOptions);
    } else {
      const specUrl = httpUrl.split('#')[0];
      urlInfo.specUrl = this.lmUrlUtilService.updateUrlParams(specUrl, { output: 'spec' });
    }

    return urlInfo;
  }

  private getBaseUrlInfoFromListSpec(listSpec: LMListSpec, options?: LMListUrlInfoOptions): LMBaseUrlInfo {
    let basePath = listSpec.basePath;
    if (!basePath) {
      basePath = this.buildBasePathFromLocation();
    }
    const resource = new KeyedResource({ resourceType: 'list', resourceId: listSpec.resourceId });

    let urlParams: Params = {};
    let sourceResource: KeyedResource;
    if (options?.pagePaneSpec?.relationResourceId) {
      sourceResource = new KeyedResource({ resourceType: 'relation', resourceId: options.pagePaneSpec.relationResourceId });
    } else if (listSpec.sourceResourceType && listSpec.sourceResourceId) {
      sourceResource = new KeyedResource({ resourceType: listSpec.sourceResourceType, resourceId: listSpec.sourceResourceId });
    }    

    const isGenUrl = this.lmContextService.isGenProductLine();
    const urlInfo: LMBaseUrlInfo = { basePath, resource, sourceResource, isGenUrl};

    // ------------------------------------------------------------------
    // no need to do comparison for base path or any url as all we are
    // looking for here are resource and sourceResource ids.
    // ------------------------------------------------------------------
    if (options?.getResourceAndSourceResourceOnly) {
      return urlInfo;
    }
  
    this.fillOutListUrls(urlInfo, listSpec, options);
    return urlInfo;
  }

  private getBaseUrlInfoFromFormSpec(formSpec: LMFormSpec, options?: LMFormUrlInfoOptions): LMBaseUrlInfo {
    let basePath = formSpec.basePath;
    if (!basePath) {
      basePath = this.buildBasePathFromLocation();
    }
    const resource = new FormResource({ resourceId: formSpec.resourceId });

    // const action = options ? && options.formPanelActionIs ? '{actionName}' : undefined;
    let action;
    if (options) {
      if (options.formPanelActionIs) {
        action = '{actionName}';
      } else if (options.fillOutTransactionUrlOnly) {
        action = formSpec.action; // todo spec-url-refactor may need to un-deprecate this just for the form-request.service scenario
      }
    }

    let sourceResource;
    if (formSpec.sourceResourceType && formSpec.sourceResourceType) {
      sourceResource = new KeyedResource({ resourceType: formSpec.sourceResourceType, resourceId: formSpec.sourceResourceId });
    }
    const calledFrom = 'getBaseUrlInfoFromFormSpec';
    const urlInfo: LMBaseUrlInfo = { basePath, resource, sourceResource, action, calledFrom };
    if (options && options.skipFilloutUrls) {
      return urlInfo;
    }

    if (formSpec.isImplicit) {
      // --------------------------------------------------------------------------------
      // isImplicit is used for formSpecs that are built on the fly on the client side.
      // the do not contain urls so don't fill out urls here.
      // --------------------------------------------------------------------------------
      return urlInfo;
    }

    if (calledFrom) {
      urlInfo.calledFrom = calledFrom;
    }

    if (options?.fillOutTransactionUrlOnly) {
      urlInfo.fillOutTransactionUrlOnly = options.fillOutTransactionUrlOnly;
    }

    this.fillOutFormUrls(urlInfo, formSpec, options);
    return urlInfo;
  }

  private getSpecUrl(urlInfo: LMBaseUrlInfo, urlInfoOptions?: LMUrlInfoOptions): string {
    // -----------------------------------------------------------------------
    // build the resource object to use in removing the keys section.
    // -----------------------------------------------------------------------
    const specResourceId = urlInfo.resource.busClassName + (urlInfo.resource.uiResourceName ? '.' + urlInfo.resource.uiResourceName : '');

    // -----------------------------------------------------------------------
    // now we can build our spec url path.
    // -----------------------------------------------------------------------
    let specUrl = urlInfo.basePath + '/' + urlInfo.resource.resourceType + '/' + specResourceId;
    const specUrlParams: LMUrlParams = { output: 'spec' };
    if (urlInfo.action) {
      specUrlParams.action = urlInfo.action;
    }

    if (urlInfoOptions && urlInfoOptions.includeNavResourceIsSpecUrl) {
      if (urlInfo.sourceResource.resourceType === AppConstants.RESOURCETYPE_NAVIGATION) {
        specUrlParams[AppConstants.RESOURCETYPE_NAVIGATION] = urlInfo.sourceResource.generateResourceId(true);
      }
    }

    // ----------------------------------------------------------------------------------------------
    // User folder will return a different spec so include it in the spec url.
    // ----------------------------------------------------------------------------------------------

    // const userfolder = urlInfo.urlParams['userfolder'];
    const userfolder = this.lmUrlUtilService.getUrlParamCaseInsensitive(urlInfo.urlParams, 'userfolder');
    if (userfolder) {
      specUrlParams.userfolder = userfolder;
    }

    // ----------------------------------------------------------------------------------------------
    // TODO type will return a slightly different spec where the listType is set. Wondering if we could
    //  just use the UrlInfo.urlParams['type'] determing it instead of relying on the returned spec.
    // ----------------------------------------------------------------------------------------------
    if (urlInfo.urlParams['type']) {
      specUrlParams.type = urlInfo.urlParams['type'];
    }

    // ----------------------------------------------------------------------------------------------
    // Mingle widget returns slight differences in the spec for lists.
    // todo: cheryl not many diffs in the spec with mw=true, consider refactoring - Phillip 10/16/23
    //  This only seems to apply to lists. mw=true for forms returns no different in the spec.
    // ----------------------------------------------------------------------------------------------
    if (urlInfo.urlParams['mw'] === 'true' || urlInfo.urlParams['mw'] === true) {
      specUrlParams['mw'] = true;
    }

    // NOTE select is just a sourceResourceId. It should not be required when getting the jsonSpec.
    // if (urlInfo.urlParams['select']) {
    //   specUrlParams.select = urlInfo.urlParams['select'];
    //
    //   if (urlInfo.urlParams['selectLink']) {
    //     specUrlParams.selectLink = urlInfo.urlParams['selectLink'];
    //   }
    // }


    // ----------------------------------------------------------------------------------------------
    // Not really used on server, just here so that the spec url in unique by financeEnterpriseGroup
    // for the purpose of url/response caching of jsonSpecs for both Conditional GETs and "edge caching".
    // EXPLANATION: For FEGs the server will return different CodeBlock specs.
    // ----------------------------------------------------------------------------------------------
    specUrlParams._feg = this.lmContextService.getContext().financeEnterpriseGroup;

    if (urlInfo.wfmDrillback) {
      specUrlParams.wfmDrillback = urlInfo.wfmDrillback;
    }

    // ----------------------------------------------------------------------------------------------
    // must include any csks that will return different specs, for now adding all csks from
    // global context so we don't break anything.
    // todo look to include only those csks that could return a different spec.
    // ----------------------------------------------------------------------------------------------
    const globalContextCsks = this.lmContextService.getContext().contextSessionKeys;
    Object.keys(globalContextCsks).forEach(key => specUrlParams[key] = globalContextCsks[key]);

    // ------------------------------------------------------------------------------
    // for mingle drillback and custom code urls have to propagate their urls csks
    // so we don't break backward compatibility.
    //
    // todo: The goal is to disallow csks from drillback and custom code urls but
    //  we cannot at the moment due to the risk of breaking customers.
    // ------------------------------------------------------------------------------
    const urlCsks: Params = this.lmUrlUtilService.getPrefixedParamsFromParams(urlInfo.urlParams, 'csk.');
    Object.keys(urlCsks).map(key => specUrlParams[key] = urlCsks[key]);

    specUrl = this.lmUrlUtilService.updateUrlParams(specUrl, specUrlParams);
    return specUrl;
  }

  public fillOutPageUrls(pageUrlInfo: LMPageUrlInfo, pageSpec: LMPageSpec): void {
    pageUrlInfo.contextSessionKeys = { ...pageSpec.contextSessionKeys };
    pageUrlInfo.initialDataUrl = this.lmUrlUtilService.buildPageInitialDataUrl(pageUrlInfo, pageSpec);
    pageUrlInfo.relActionUrl = this.lmUrlUtilService.buildRelationActionUrl(pageUrlInfo);
  }

  public fillOutListUrls(listUrlInfo: LMListUrlInfo, listSpec: LMListSpec, options?: LMListUrlInfoOptions): void {
    if (this.lmContextService.useNewSpecUrls()) {
      const pagesize = this.lmUrlUtilService.getUrlParamCaseInsensitive(listUrlInfo.urlParams, 'pagesize');
      if (pagesize) {
        listSpec.pageSize = pagesize;
      }
    }

    // crucial for mingle drill back message that contain extra valid csks, especially for csk.gen.
    // todo: need to include nc. value as well. Maybe rename this to extraParams.
    listUrlInfo.contextSessionKeys = { ...listSpec.contextSessionKeys };

    if (!this.lmContextService.useNewSpecUrls()) {
      this.compareSpecPart(listUrlInfo, 'resourceId', listSpec.resourceId, listUrlInfo.resource.resourceId);

      if (listUrlInfo.sourceResource || listSpec.sourceResourceType) {
        const clientSourceResource = `${listUrlInfo?.sourceResource.resourceType}=${listUrlInfo?.sourceResource?.resourceId}`;
        const serverSourceResource = `${listSpec.sourceResourceType}=${listSpec.sourceResourceId}`;
        this.compareSpecPart(listUrlInfo, 'sourceResource', serverSourceResource, clientSourceResource);
      }
    }

    listUrlInfo.initialDataUrl = this.lmUrlUtilService.buildListInitialDataUrl(listUrlInfo, listSpec, options);
    listUrlInfo.transactionUrl = this.lmUrlUtilService.buildListTransactionUrl(listUrlInfo, listSpec);
    listUrlInfo.viewAuditLogRedirectUrl = this.lmUrlUtilService.buildViewAuditLogRedirectUrl(listUrlInfo);
    listUrlInfo.navigationUrl = this.lmUrlUtilService.buildNavigationUrl(listUrlInfo);

    if (listSpec.busListSpec.implementsTreeView && !listSpec.busListSpec.implementsCubeView) {
      listUrlInfo.childrenUrl = this.lmUrlUtilService.buildHierarchyChildrenUrl(listUrlInfo);
      if (listSpec.busListSpec.implementsOrgChartView) {
        listUrlInfo.centeredUrl = this.lmUrlUtilService.buildHierarchyCenteredUrl(listUrlInfo);
      }
    }

    listUrlInfo.actionRedirectUrl = this.lmUrlUtilService.buildActionRedirectUrl(listUrlInfo, listSpec.isTemplateList);
    listUrlInfo.openActionRedirectUrl = this.lmUrlUtilService.buildActionRedirectUrl(listUrlInfo, listSpec.isTemplateList);
    listUrlInfo.reportPersonalizationUrl = this.lmUrlUtilService.buildReportPersonalizationUrl(listUrlInfo);
    listUrlInfo.listPersonalizationUrl = this.lmUrlUtilService.buildListPersonalizationUrl(listUrlInfo, listSpec);
    listUrlInfo.fieldLookUpUrl = this.lmUrlUtilService.buildFieldLookupUrl(listUrlInfo);
    listUrlInfo.genAIPromptUrl = this.lmUrlUtilService.buildGenAIPromptUrl(listUrlInfo);
    listUrlInfo.fieldSearchUrl = this.lmUrlUtilService.buildFieldSearchUrl(listUrlInfo);
    listUrlInfo.relatedReportSourceUrl = this.lmUrlUtilService.buildRelatedReportSourceUrl(listUrlInfo, listSpec);
    listUrlInfo.relActionUrl = this.lmUrlUtilService.buildRelationActionUrl(listUrlInfo);
    listUrlInfo.actorGroupSelectUrl = this.lmUrlUtilService.buildActorGroupSelectUrl(listUrlInfo);
    listUrlInfo.roleSelectUrl = this.lmUrlUtilService.buildRoleSelectUrl(listUrlInfo);
    listUrlInfo.roleGroupSelectUrl = this.lmUrlUtilService.buildRoleGroupSelectUrl(listUrlInfo);

    // this.buildSortUrls(listUrlInfo, listSpec);

    // Append Cube parameters for cube drill
    if (listUrlInfo.urlParams && listUrlInfo.urlParams['cubeDrill']) { // this.lmUrlUtilService.hasParam(listUrlInfo.httpUrl, 'cubeDrill'))
      if (this.lmContextService.useNewSpecUrls()) {
        listUrlInfo.cubeParams = this.getCubeParams(listUrlInfo.httpUrl);
      } else {
        listUrlInfo.cubeParams = (listSpec as any).cubeOptions.cubeParams;
      }
    }
    
    if (options?.isArchiveData) {
      listUrlInfo.isArchiveData = true;
    }

    if (!this.lmContextService.useNewSpecUrls()) {
      this.compareSpecUrls(listUrlInfo, `initialDataUrl`, (listSpec as any).initialDataUrl, listUrlInfo.initialDataUrl, options);
      if (listSpec.listType !== 'filterlist') {
        this.compareSpecUrls(listUrlInfo, `transactionUrl`, (listSpec as any).transactionUrl, listUrlInfo.transactionUrl, options);
        this.compareSpecUrls(listUrlInfo, `viewAuditLogRedirectUrl`, (listSpec as any).viewAuditLogRedirectUrl, listUrlInfo.viewAuditLogRedirectUrl, options);
        this.compareSpecUrls(listUrlInfo, `navigationUrl`, (listSpec as any).navigationUrl, listUrlInfo.navigationUrl, options);
        this.compareSpecUrls(listUrlInfo, `childrenUrl`, (listSpec as any).childrenUrl, listUrlInfo.childrenUrl, options);
        this.compareSpecUrls(listUrlInfo, `centeredUrl`, (listSpec as any).centeredUrl, listUrlInfo.centeredUrl, options);
        this.compareSpecUrls(listUrlInfo, `actionRedirectUrl`, (listSpec as any).actionRedirectUrl, listUrlInfo.actionRedirectUrl, options);
        // todo openActionRedirectUrl is identical to actionRedirectUrl - remove this.
        this.compareSpecUrls(listUrlInfo, `openActionRedirectUrl`, (listSpec as any).openActionRedirectUrl, listUrlInfo.openActionRedirectUrl, options);
        this.compareSpecUrls(listUrlInfo, `reportPersonalizationUrl`, (listSpec as any).reportPersonalizationUrl, listUrlInfo.reportPersonalizationUrl, options);
        this.compareSpecUrls(listUrlInfo, `listPersonalizationUrl`, (listSpec as any).listPersonalizationUrl, listUrlInfo.listPersonalizationUrl, options);
        this.compareSpecUrls(listUrlInfo, `fieldLookupUrl`, (listSpec as any).fieldLookUpUrl, listUrlInfo.fieldLookUpUrl, options);
        this.compareSpecUrls(listUrlInfo, `fieldSearchUrl`, (listSpec as any).fieldSearchUrl, listUrlInfo.fieldSearchUrl, options);
        this.compareSpecUrls(listUrlInfo, `actorGroupSelectUrl`, (listSpec as any).actorGroupSelectUrl, listUrlInfo.actorGroupSelectUrl, options);
        this.compareSpecUrls(listUrlInfo, `relatedReportSourceUrl`, (listSpec as any).relatedReportSourceUrl, listUrlInfo.relatedReportSourceUrl, options);
      }

      listUrlInfo.initialDataUrl = (listSpec as any).initialDataUrl;
      listUrlInfo.transactionUrl = (listSpec as any).transactionUrl;
      listUrlInfo.viewAuditLogRedirectUrl = (listSpec as any).viewAuditLogRedirectUrl;
      listUrlInfo.navigationUrl = (listSpec as any).navigationUrl;
      listUrlInfo.childrenUrl = (listSpec as any).childrenUrl;
      listUrlInfo.centeredUrl = (listSpec as any).centeredUrl;
      listUrlInfo.actionRedirectUrl = (listSpec as any).actionRedirectUrl;
      listUrlInfo.openActionRedirectUrl = (listSpec as any).openActionRedirectUrl;
      listUrlInfo.reportPersonalizationUrl = (listSpec as any).reportPersonalizationUrl;
      listUrlInfo.listPersonalizationUrl = (listSpec as any).listPersonalizationUrl;
      listUrlInfo.fieldLookUpUrl = (listSpec as any).fieldLookUpUrl;
      listUrlInfo.fieldSearchUrl = (listSpec as any).fieldSearchUrl;
      listUrlInfo.actorGroupSelectUrl = (listSpec as any).actorGroupSelectUrl;
      listUrlInfo.relatedReportSourceUrl = (listSpec as any).relatedReportSourceUrl;
      listUrlInfo.relActionUrl = (listSpec as any).relActionUrl;
    }
  }

  public fillOutFormUrls(formUrlInfo: LMFormUrlInfo, formSpec: LMFormSpec, formUrlOptions?: LMFormUrlInfoOptions): void {
    // crucial for mingle drill back message that contain extra valid csks, especially for csk.gen.
    // todo: need to include nc. value as well. Maybe rename this to extraParams.
    formUrlInfo.contextSessionKeys = { ...formSpec.contextSessionKeys };

    // todo spec-url-refactor not sure about how to handle buildUrls for these types.
    //   should probably handle in the build url functions themselves?!?
    // type LMFormType = 'Action' | 'Chart' | 'Composite' | 'Matrix' | 'Menu' | 'NotAForm' |
    //   'PageContext' | 'Standard' | 'Search' | 'Summary' | 'Wizard';
    if (formSpec.busFormSpec.formType === 'Menu') {
      return;
    }

    if (!this.lmContextService.useNewSpecUrls()) {
      this.compareSpecPart(formUrlInfo, 'resourceId', (formSpec as any).resourceId, formUrlInfo.resource.resourceId);

      // Thinking this is an invalid comparison as we never send the action on the spec. We do use the
      // formUrlInfo.action to help build the formUrlInfo urls and the FormSpec urls.
      // this.compareSpecPart(formUrlInfo, 'action', (formSpec as any).action, formUrlInfo.action);

      if (formUrlInfo.sourceResource || (formSpec as any).sourceResourceType) {
        this.compareSpecPart(formUrlInfo, 'sourceResourceType', (formSpec as any).sourceResourceType, formUrlInfo.sourceResource ? formUrlInfo.sourceResource.resourceType : undefined);
        this.compareSpecPart(formUrlInfo, 'sourceResourceId', (formSpec as any).sourceResourceId, formUrlInfo.sourceResource ? formUrlInfo.sourceResource.resourceId : undefined);
      }
    }

    const buildTransactionUrlOnly = formUrlOptions && formUrlOptions.fillOutTransactionUrlOnly;

    if (formUrlOptions?.isArchiveData) {
      formUrlInfo.isArchiveData = true;
    }

    let buildInitialDataUrl = true;
    if (buildTransactionUrlOnly || (formUrlOptions && formUrlOptions.isCompoundFieldForm)) {
      buildInitialDataUrl = false;
    }

    if (buildInitialDataUrl) {
      formUrlInfo.initialDataUrl = this.lmUrlUtilService.buildFormInitialDataUrl(formUrlInfo, formSpec);
    }

    // ---------------------------------------------------------------
    // form requests only have transaction url so don't try and build
    // all the others if this urlInfo is for a formRequest
    // ---------------------------------------------------------------
    formUrlInfo.transactionUrl = this.lmUrlUtilService.buildFormTransactionUrl(formUrlInfo, formSpec);

    if (!buildTransactionUrlOnly) {
      formUrlInfo.matrixCellTransactionUrl = this.lmUrlUtilService.buildMatrixCellTransactionUrl(formUrlInfo, formSpec);
      formUrlInfo.matrixColumnTransactionUrl = this.lmUrlUtilService.buildMatrixColumnTransactionUrl(formUrlInfo, formSpec);
      formUrlInfo.formInvokeCreateUrl = this.lmUrlUtilService.buildFormInvokeCreateUrl(formUrlInfo);
      formUrlInfo.actionRedirectUrl = this.lmUrlUtilService.buildActionRedirectUrl(formUrlInfo);
      formUrlInfo.navigationUrl = this.lmUrlUtilService.buildNavigationUrl(formUrlInfo);
      formUrlInfo.relActionUrl = this.lmUrlUtilService.buildRelationActionUrl(formUrlInfo);
      formUrlInfo.formRequestUrl = this.lmUrlUtilService.buildFormRequestUrl(formUrlInfo, formSpec);
      formUrlInfo.viewAuditLogRedirectUrl = this.lmUrlUtilService.buildViewAuditLogRedirectUrl(formUrlInfo);
      formUrlInfo.previewActionUrl = this.lmUrlUtilService.buildPreviewActionUrl(formUrlInfo);
    }

    if (!this.lmContextService.useNewSpecUrls()) {
      if (buildInitialDataUrl) {
        // ---------------------------------------------------------------
        // if not building initialDataUrl on client then don't compare
        // against one delivered from server.
        // ---------------------------------------------------------------
        this.compareSpecUrls(formUrlInfo, 'initialDataUrl', (formSpec as any).initialDataUrl, formUrlInfo.initialDataUrl, formUrlOptions);
        formUrlInfo.initialDataUrl = (formSpec as any).initialDataUrl;
      }

      this.compareSpecUrls(formUrlInfo, 'transactionUrl', (formSpec as any).transactionUrl, formUrlInfo.transactionUrl, formUrlOptions);
      formUrlInfo.transactionUrl = (formSpec as any).transactionUrl;

      if (!buildTransactionUrlOnly) {
        this.compareSpecUrls(formUrlInfo, 'matrixCellTransactionUrl', (formSpec as any).matrixCellTransactionUrl, formUrlInfo.matrixCellTransactionUrl, formUrlOptions);
        this.compareSpecUrls(formUrlInfo, 'matrixColumnTransactionUrl', (formSpec as any).matrixColumnTransactionUrl, formUrlInfo.matrixColumnTransactionUrl, formUrlOptions);
        this.compareSpecUrls(formUrlInfo, 'formInvokeCreateUrl', (formSpec as any).formInvokeCreateUrl, formUrlInfo.formInvokeCreateUrl, formUrlOptions);
        this.compareSpecUrls(formUrlInfo, 'actionRedirectUrl', (formSpec as any).actionRedirectUrl, formUrlInfo.actionRedirectUrl, formUrlOptions);
        this.compareSpecUrls(formUrlInfo, 'navigationUrl', (formSpec as any).navigationUrl, formUrlInfo.navigationUrl, formUrlOptions);
        this.compareSpecUrls(formUrlInfo, 'relActionUrl', (formSpec as any).relActionUrl, formUrlInfo.relActionUrl, formUrlOptions);
        this.compareSpecUrls(formUrlInfo, 'formRequestUrl', (formSpec as any).formRequestUrl, formUrlInfo.formRequestUrl, formUrlOptions);
        this.compareSpecUrls(formUrlInfo, 'viewAuditLogRedirectUrl', (formSpec as any).viewAuditLogRedirectUrl, formUrlInfo.viewAuditLogRedirectUrl, formUrlOptions);
        this.compareSpecUrls(formUrlInfo, 'previewActionUrl', (formSpec as any).previewActionUrl, formUrlInfo.previewActionUrl, formUrlOptions);

        formUrlInfo.matrixCellTransactionUrl = (formSpec as any).matrixCellTransactionUrl;
        formUrlInfo.matrixColumnTransactionUrl = (formSpec as any).matrixColumnTransactionUrl;
        formUrlInfo.formInvokeCreateUrl = (formSpec as any).formInvokeCreateUrl;
        formUrlInfo.actionRedirectUrl = (formSpec as any).actionRedirectUrl;
        formUrlInfo.navigationUrl = (formSpec as any).navigationUrl;
        formUrlInfo.relActionUrl = (formSpec as any).relActionUrl;
        formUrlInfo.formRequestUrl = (formSpec as any).formRequestUrl;
        formUrlInfo.viewAuditLogRedirectUrl = (formSpec as any).viewAuditLogRedirectUrl;
        formUrlInfo.previewActionUrl = (formSpec as any).previewActionUrl;
      }

      formUrlInfo.navigationContext = (formSpec as any).navigationContext;
      formUrlInfo.fromInbox = (formSpec as any).fromInbox;
      formUrlInfo.processFlow =  (formSpec as any).processFlow;
    }
  }

  /**
   * Get an object of the source resourceType and id strings by searching in the url params.
   * @param httpUrl The url used to find the source resource id.
   */
  private getSourceResource(urlParams: Params): Resource {
    const resourceType = this.getSourceResourceType(urlParams);
    if (resourceType === null) {
      return null;
    }

    const resourceId = urlParams[resourceType];
    if (resourceType === 'bidrill') {
      return new BIDrillResource( { resourceId });
    }
    return new KeyedResource({ resourceType, resourceId });
  }

  private getResourceFromParams(urlParams: Params, resourceType: string): KeyedResource {
    // resourceType = Object.keys(urlParams).find(key => key.toLowerCase() === resourceType.toLowerCase());
    // if (!resourceType) {
    //   return null;
    // }
    //
    // const resourceId = urlParams[resourceType];
    const resourceId = this.lmUrlUtilService.getUrlParamCaseInsensitive(urlParams, resourceType);
    if (!resourceId) {
      return null;
    }

    return new KeyedResource({ resourceType, resourceId });
  }

  /**
   * Get the source resource type by looking specifically for navigation, drill, and
   * relation in the url params keys.
   * @param urlParams
   */
  private getSourceResourceType(urlParams: Params): string {
    // NOTE the order here should match the order in ControllerBase#setSourceResourceInfo
    if (urlParams[ AppConstants.RESOURCETYPE_RELATION ]) {
      return AppConstants.RESOURCETYPE_RELATION;
    }

    if (urlParams[ AppConstants.RESOURCETYPE_NAVIGATION ]) {
      return AppConstants.RESOURCETYPE_NAVIGATION;
    }

    if (urlParams[ AppConstants.RESOURCETYPE_DRILL ]) {
      return AppConstants.RESOURCETYPE_DRILL;
    }

    if (urlParams[ AppConstants.RESOURCETYPE_PAGEPANEL ]) {
      return AppConstants.RESOURCETYPE_PAGEPANEL;
    }

    if (urlParams[ AppConstants.RESOURCETYPE_LIST ]) {
      return AppConstants.RESOURCETYPE_LIST;
    }

    if (urlParams[ AppConstants.RESOURCETYPE_MENU ]) {
      return AppConstants.RESOURCETYPE_MENU;
    }

    if (urlParams[ AppConstants.RESOURCETYPE_PAGEMENU ]) {
      return AppConstants.RESOURCETYPE_PAGEMENU;
    }

    if (urlParams[ AppConstants.RESOURCETYPE_FORMPANEL ]) {
      return AppConstants.RESOURCETYPE_FORMPANEL;
    }

    if (urlParams[ AppConstants.RESOURCETYPE_SUBPANEL ]) {
      return AppConstants.RESOURCETYPE_SUBPANEL;
    }

    if (urlParams[ AppConstants.RESOURCETYPE_FORMINVOKE ]) {
      return AppConstants.RESOURCETYPE_FORMINVOKE;
    }

    if (urlParams[ AppConstants.RESOURCETYPE_SELECT ]) {
      return AppConstants.RESOURCETYPE_SELECT;
    }

    if (urlParams[ AppConstants.RESOURCETYPE_PARENT ]) {
      return AppConstants.RESOURCETYPE_PARENT;
    }

    if (urlParams[ AppConstants.RESOURCETYPE_BIDRILL ]) {
      return AppConstants.RESOURCETYPE_BIDRILL;
    }

    if (urlParams[ AppConstants.RESOURCETYPE_PAGE ]) {
      return AppConstants.RESOURCETYPE_PAGE;
    }

    return null;
  }

  private buildBasePathFromHref(httpUrl: string): string {
    const isGenUrl: boolean = this.lmUrlUtilService.isGenUrlUsingHref(httpUrl);
    const pathname: string = this.lmUrlUtilService.getUrlPath(httpUrl);
    const webApp = this.lmUrlUtilService.getUrlPathValue(pathname, AppConstants.URLSEG_WEBAPP_INDEX, isGenUrl);
    if (isGenUrl || this.lmContextService.isGenProductLine()) {
      return '/' + webApp;
    }

    const dataArea = this.lmUrlUtilService.getUrlPathValue(pathname, AppConstants.URLSEG_PDL_OR_DA_INDEX, isGenUrl);
    return '/' + dataArea + '/' +  webApp;
  }

  private buildBasePathFromLocation(): string {
    // need to get base path from location since the global context isn't always changed when running
    // a URL. For example when searching for something in the app menu and clicking a menu item from
    // another web app.
    const isGenUrl: boolean = this.lmUrlUtilService.isGenUrlUsingWindowLocation();

    const webApp = this.lmUrlUtilService.getUrlPathValue(window.location.pathname, AppConstants.URLSEG_WEBAPP_INDEX, isGenUrl);
    if (isGenUrl || this.lmContextService.isGenProductLine()) {
      return '/' + webApp;
    }

    const dataArea = this.lmUrlUtilService.getUrlPathValue(window.location.pathname, AppConstants.URLSEG_PDL_OR_DA_INDEX, isGenUrl);
    return '/' + dataArea + '/' +  webApp;
  }

  private getCubeParams(httpUrl: string): LMCubeUrlParams {
    const url: URL = this.lmUrlUtilService.buildURLFromHref(httpUrl);
    const cubeParams: LMCubeUrlParams = {};
    cubeParams.cubeDrill = true;
    cubeParams.isFactRowElement = url.searchParams.get('isFactRowElement') === 'true';
    cubeParams.cubeMediator = url.searchParams.get('cubeMediator');
    cubeParams.cubeNm = url.searchParams.get('cubeNm');
    cubeParams.cubeFld = url.searchParams.get('cubeFld');

    if (url.searchParams.has('addtlFilter')) {
      cubeParams.addtlFilter = url.searchParams.get('addtlFilter');
    }
    if (url.searchParams.has('reportingBasis')) {
      cubeParams.reportingBasis = url.searchParams.get('reportingBasis');
    }
    if (url.searchParams.has('periodKey')) {
      cubeParams.periodKey = url.searchParams.get('periodKey');
    }
    if (url.searchParams.has('periodView')) {
      cubeParams.periodView = url.searchParams.get('periodView');
    }
    if (url.searchParams.has('pvPeriodIdx')) {
      cubeParams.pvPeriodIdx = parseInt(url.searchParams.get('pvPeriodIdx'), 10);
    }
    if (url.searchParams.has('pivotView')) {
      cubeParams.pivotView = url.searchParams.get('pivotView');
    }
    if (url.searchParams.has('columnView')) {
      cubeParams.columnView = url.searchParams.get('columnView');
    }

    for (let i = 0; ; i++) {
      const dimId = url.searchParams.get('td' + i) || undefined;
      if (dimId === undefined) {
        break;
      }
      const dimVal = url.searchParams.get('tdV' + i);
      if (i === 0) {
        cubeParams.topNodeDims = [];
      }
      cubeParams.topNodeDims.push({ td: dimId, tdV: dimVal });
    }

    for (let i = 0; ; i++) {
      const topcFldId = url.searchParams.get('tc' + i) || undefined;
      if (topcFldId === undefined) {
        break;
      }
      const topcFldVal = url.searchParams.get('tcV' + i);
      if (i === 0) {
        cubeParams.topContextFields = [];
      }
      cubeParams.topContextFields.push({ tc: topcFldId, tcV: topcFldVal });
    }

    for (let i = 0; ; i++) {
      const dimId = url.searchParams.get('dd' + i) || undefined;
      if (dimId === undefined) {
        break;
      }
      const dimVal = url.searchParams.get('ddV' + i);
      const dimFixed = url.searchParams.get('ddFix' + i);
      const dimTotal = url.searchParams.get('ddTot' + i);
      const dimType = url.searchParams.get('ddType' + i);
      if (i === 0) {
        cubeParams.dimensions = [];
      }
      cubeParams.dimensions.push({ dd:dimId, ddV:dimVal, ddFix: dimFixed === 'true', ddTot: dimTotal === 'true', ddType: dimType });
    }

    for (let i = 0; ; i++) {
      const dimId = url.searchParams.get('cd' + i) || undefined;
      if (dimId === undefined) {
        break;
      }
      const dimVal = url.searchParams.get('cdV' + i);
      const dimFixed = url.searchParams.get('cdFix' + i);
      const dimTotal = url.searchParams.get('cdTot' + i);
      const dimType = url.searchParams.get('cdType' + i);
      if (i === 0) {
        cubeParams.ctxDimensions = [];
      }
      cubeParams.ctxDimensions.push({ cd: dimId, cdV: dimVal, cdFix: dimFixed === 'true', cdTot: dimTotal === 'true', cdType: dimType });
    }

    for (let i = 0; ; i++) {
      const dimId = url.searchParams.get('fd' + i) || undefined;
      if (dimId === undefined) {
        break;
      }
      const dimVal = url.searchParams.get('fdV' + i);
      const dimFixed = url.searchParams.get('fdFix' + i);
      const dimTotal = url.searchParams.get('fdTot' + i);
      const dimType = url.searchParams.get('fdType' + i);
      if (i === 0) {
        cubeParams.fltrDimensions = [];
      }
      cubeParams.fltrDimensions.push({ fd: dimId, fdV: dimVal, fdFix: dimFixed === 'true', fdTot: dimTotal === 'true', fdType: dimType });
    }

    return cubeParams;
  }

  public compareSpecUrls(baseUrlInfo: LMBaseUrlInfo, name: string, serverHref: string, clientHref: string, compareOptions?: LMCompareUrlOptions) {
    try {
      if (!serverHref && !clientHref) {
        return; // no op if they are both empty.
      }

      if (serverHref) {
        serverHref = this.lmUrlUtilService.toRelativeUrl(serverHref);
      }

      const diffs: string[][] = [];
      if (serverHref && clientHref) {
        this.comparePathname(diffs, this.lmUrlUtilService.getUrlPath(clientHref), this.lmUrlUtilService.getUrlPath(serverHref));
        this.compareUrlParams(diffs, baseUrlInfo, clientHref, serverHref, compareOptions);
      } else {
        diffs.push([
          `clientUrl: ${clientHref}`,
          `serverUrl: ${serverHref}`
        ]);
      }

      if (diffs.length > 0) {
        this.processCompareDiffs(name, diffs, baseUrlInfo, clientHref, serverHref);
      }

    } catch (error) {
      const jsonLog = {
        message: `${LMUrlInfoBuilderService.ServerLogVersionId}: ${LMUrlInfoBuilderService.PrefixString}: Exception in compareSpecUrls: ${name} - ${error.stack}`,
        clientUrl: `${clientHref}`,
        serverUrl: `${serverHref}`,
        windowLocationHref: window.location.href,
        stacktrace: `${error.stack}`,
        referrer: document.referrer,
        urlInfo: this.baseUrlData(baseUrlInfo)
      };

      console.error(jsonLog);
      this.lmLoggerService.logToServerConsole(JSON.stringify(jsonLog));
    }
  }

  private paramsAreEqual(paramName: string, clientParams: string[], serverParams: string[]) {
    if (clientParams.length !== serverParams.length) {
      // --------------------------------------------------------------------------------------------
      // Workaround for https://jira.infor.com/browse/LMWEB-20223 - mingle and portal remove empty
      // 'additional params' from the portal application definition when initializing lmclient
      // in the portal. Infor Go, however, does not, which causes url mismatches on a csk.
      // See https://inforwiki.atlassian.net/browse/SCRMPORTALV2-8493
      //
      // The mismatch happens because the app has been initialized with the empty csk param
      // (csk.HROrganization=) but when a drill back happens (from a portal widget or drillback)
      // empty params are removed. This issue can cause tons of logToConsole calls, 1 for each
      // url in a composite form and all it's lists. These logToConsole calls result in POST
      // message being sent to the back. In the case of EmployeeProfile it's over 1000 POST
      // calls.
      // --------------------------------------------------------------------------------------------
      if (paramName?.startsWith('csk.')) {
        if (serverParams.length === 0
        && (clientParams.length === 1 && clientParams[0] === '')) {
          return true;
        }
      }
      return false;
    }

    try {
      // --------------------------------------------------------------------------------------------
      // Attempt to decode certain params to compare them. The server and client encoders encode
      // differently so encoded params often show diffs. Once they are decoded they no longer show
      // all the diffs in the sumo logs. But if there is a diff after decoding then that is a good
      // indication that those params are in fact different.
      // --------------------------------------------------------------------------------------------
      if (this.isPotentiallyEncodedParam(paramName)) {
        // only checking urls param with a single value. That avoids item for matrix
        // and fld which isn't an encoded param anyway.
        if (clientParams.length === 1 && serverParams.length === 1) {
          const encodedClientParam: string = clientParams[0];
          const encodedServerParam: string = serverParams[0];

          // if encoded values equal then no need to decode.
          if (encodedClientParam && encodedServerParam && !isEqual(encodedClientParam, encodedServerParam)) {
            const decodedClientParam = decodeURIComponent(encodedClientParam);
            const decodedServerParam = decodeURIComponent(encodedServerParam);
            return isEqual(decodedClientParam, decodedServerParam);
          }
        }
      }
    } catch (e) {
      // safety - noop - let the code below execute.
    }

    /**
     * I'm not completely sure why sort() is used here. The
     * only thing to sort would be a fld and item which can
     * have multiple values on the url.
     */
    let sortedClientParams: string[] = clientParams.sort();
    const sortedServerParams: string[] = serverParams.sort();

    if (paramName === 'fld') {
      sortedClientParams = sortedClientParams.map((clientParam: string, index: number) => {
        const serverParam = sortedServerParams[index];

        if (clientParam === serverParam
          ||  clientParam.indexOf('_col_') < 0) {
          return clientParam;
        }

        const newClientParam = clientParam.replace('_col_', '_prd_');
        if (newClientParam === serverParam) {
          return newClientParam;
        }

        return clientParam;
      });
    }

    return isEqual(sortedClientParams, sortedServerParams);
  }

  /**
   * list of params that could possibly be encoded
   * @param paramName
   * @private
   */
  private isPotentiallyEncodedParam(paramName: string): boolean {
    if (paramName) {
      if (paramName === 'filter'
        ||  paramName === 'navigation'
        ||  paramName === 'drill'
        ||  paramName === 'relation'
        ||  paramName === 'list'
        ||  paramName === 'fromlist'
        ||  paramName === 'fk'
        ||  paramName === 'lk'
        ||  paramName === 'pk'
        ||  paramName.startsWith('fk.')
        ||  paramName.startsWith('lk.')
        ||  paramName.startsWith('pk.')
        ||  paramName.startsWith('fc.')
        ||  paramName.startsWith('nc.')
      ) {
        return true;
      }
    }

    return false;
  }

  private compareSpecPart(baseUrlInfo: LMBaseUrlInfo, partName: string, serverThing: string, clientThing: string): boolean {
    if (!serverThing && !clientThing) {
      return true; // so undefined and null will equate
    }

    let result;
    if (serverThing && clientThing) {
      result = serverThing.toLowerCase() === clientThing.toLowerCase();
    } else {
      result = serverThing === clientThing;
    }

    if (result) {
      return true;
    }

    const jsonLog = {
      message: `${LMUrlInfoBuilderService.ServerLogVersionId}: ${LMUrlInfoBuilderService.PrefixString}: Found differences in Spec vs UrlInfo: ${partName}`,
      client: `${partName}: ${clientThing}`,
      server: `${partName}: ${serverThing}`,
      windowLocationHref: window.location.href,
      referrer: document.referrer,
      // stacktrace: new Error().stack,
      urlInfo: this.baseUrlData(baseUrlInfo)
    };

    console.error(jsonLog);
    this.lmLoggerService.logToServerConsole(JSON.stringify(jsonLog));
    return false;
  }

  // private buildSortUrls(listUrlInfo: LMListUrlInfo, listSpec: LMListSpec) {
  //   if (!listSpec.busListSpec.displayFields || listSpec.busListSpec.displayFields.length === 0) {
  //     return;
  //   }
  //
  //   listSpec.busListSpec.displayFields.forEach(displayField => {
  //     if (displayField.cardViewSortOrders && displayField.cardViewSortOrders.length > 0) {
  //       displayField.cardViewSortOrders.forEach(cardViewSortOrder => {
  //         const sortUrl = this.lmUrlUtilService.buildSortUrl(listUrlInfo, listSpec, cardViewSortOrder.sortOrderName, cardViewSortOrder.isAscending);
  //
  //         if (this.lmContextService.useNewSpecUrls()) {
  //           cardViewSortOrder.sortUrl = sortUrl;
  //         } else if (this.lmContextService.compareSpecUrls()) {
  //           this.compareSpecUrls(displayField.fieldId + '.cardViewSortOrder', cardViewSortOrder.sortUrl, sortUrl);
  //         }
  //       });
  //
  //     } else if (displayField.sortOrderName) {
  //       const sortUrl = this.lmUrlUtilService.buildSortUrl(listUrlInfo, listSpec, displayField.sortOrderName, displayField.isAscending);
  //
  //       if (this.lmContextService.useNewSpecUrls()) {
  //         displayField.sortUrl = sortUrl;
  //       } else if (this.lmContextService.compareSpecUrls()) {
  //         this.compareSpecUrls(displayField.fieldId + '.sortUrl', displayField.sortUrl, sortUrl);
  //       }
  //     }
  //   });
  // }

  public baseUrlData(fullUrlInfo: LMBaseUrlInfo): any {
    const baseUrlData = {
      basePath: fullUrlInfo.basePath,
      // url: fullUrlInfo.url?.href,
      httpUrl: fullUrlInfo.httpUrl,
      specUrl: fullUrlInfo.specUrl,
      resource: fullUrlInfo.resource ? fullUrlInfo.resource.resourceId : '',
      sourceResource: fullUrlInfo.sourceResource ? fullUrlInfo.sourceResource.resourceType + '=' + fullUrlInfo.sourceResource.resourceId : '',
      listResource: fullUrlInfo.listResource ? fullUrlInfo.listResource.resourceId : '',
      fromListResource: fullUrlInfo.fromListResource ? fullUrlInfo.fromListResource.resourceId : '',
      action: fullUrlInfo.action,
      navigationContext: fullUrlInfo.navigationContext,
      fromInbox: fullUrlInfo.fromInbox,
      processFlow: fullUrlInfo.processFlow,
      calledFrom: fullUrlInfo.calledFrom,
      fillOutTransactionUrlOnly: fullUrlInfo.fillOutTransactionUrlOnly
      // stacktrace: fullUrlInfo.stacktrace
    };

    return baseUrlData;
  }

  // private formatDiffs(diffs: string[][]): string {
  //   try {
  //     const diffString: string[] = diffs.map(diff => `["${diff[0]}","${diff[1]}"]`);
  //     return diffString.join(',');
  //   } catch (e) {
  //     return 'failed formatting diff';
  //   }
  // }
  //
  private comparePathname(
    diffs: string[][],
    clientPathname: string,
    serverPathname: string
  ) {
    if (clientPathname !== serverPathname) {
      diffs.push([
        `clientUrl pathname: ${clientPathname}`,
        `serverUrl pathname: ${serverPathname}`,
      ]);
    }
  }

  private static paramsToIgnore = [
    // 'csk.ng',
    // 'csk.cli',
    // 'csk.showusingxi',
    // 'csk.ng3x',
    // 'csk.displayType',
    // 'csk.IsoLocale',
    // 'csk.JobBoard',
    // 'csk.HROrganization',
  ];

  private compareUrlParams(
    diffs: string[][],
    baseUrlInfo: LMBaseUrlInfo,
    clientHref: string,
    serverHref: string,
    compareOptions: LMCompareUrlOptions
  ): void {
    const clientUrlParamKeys: string[] = [];
    const serverUrlParamKeys: string[] = [];

    let clientUrlParams: KeyValueObject[] = [];
    let serverUrlParams: KeyValueObject[] = [];

    if (compareOptions?.getResourceAndSourceResourceOnly) {
      if (baseUrlInfo.sourceResource) {
        clientUrlParamKeys.push(baseUrlInfo.sourceResource.resourceType);
      }
    } else {
      clientUrlParams = this.getUrlParamsObjectArray(clientHref);
      serverUrlParams = this.getUrlParamsObjectArray(serverHref);

      clientUrlParams.forEach((obj: KeyValueObject) => {
        if (LMUrlInfoBuilderService.paramsToIgnore.find(pti => pti === obj.key)) {
          return;
        }

        clientUrlParamKeys.push(obj.key);
      });

      serverUrlParams.forEach((obj: KeyValueObject) => {
        if (LMUrlInfoBuilderService.paramsToIgnore.find(pti => pti === obj.key)) {
          return;
        }

        serverUrlParamKeys.push(obj.key);
      });
    }

    const allUrlParamKeys = new Set([ ... clientUrlParamKeys, ... serverUrlParamKeys ]);
    allUrlParamKeys.forEach(key => {
      const clientParams: string[] = clientUrlParams.filter((obj: KeyValueObject) => key === obj.key).map(obj => obj.value);
      const serverParams: string[] = serverUrlParams.filter((obj: KeyValueObject) => key === obj.key).map(obj => obj.value);
      if (!this.paramsAreEqual(key, clientParams, serverParams)) {
        diffs.push([
          `clientUrlParam: ${key}=${clientParams}`,
          `serverUrlParam: ${key}=${serverParams}`
        ]);
      }
    });
  }

  private processCompareDiffs(name: string, diffs: string[][], baseUrlInfo: LMBaseUrlInfo, clientHref: string, serverHref: string): void {
    if (diffs.length === 0) {
      return;
    }

    const jsonLog = {
      message: `${LMUrlInfoBuilderService.ServerLogVersionId}: ${LMUrlInfoBuilderService.PrefixString}: Found differences in url: ${name}`,
      diffs,
      clientHref,
      serverHref,
      windowLocationHref: window.location.href,
      referrer: document.referrer,
      // stacktrace: new Error().stack,
      urlInfo: this.baseUrlData(baseUrlInfo)
    };

    // only log this if there is more than 1 breadcrumb.
    // a single breadcrumb is the page we are on when the breadcrumbs start over.
    if (this.lmContextService.showBreadcrumbs() && this.lmBreadcrumbService?.breadcrumbs?.length > 1) {
      jsonLog['breadcrumbs'] = this.lmBreadcrumbService.breadcrumbs.map((breadCrumb: SohoBreadcrumbItemOptions) =>
          ({ content: breadCrumb.content, href: breadCrumb.href }));
    }

    console.error(jsonLog);
    // const diffString = diffs.map(a => a.join(': ')).join('; ');
    // const message = jsonLog.message + ' - ' + diffString;
    // console.log(message);

    this.lmLoggerService.logToServerConsole(JSON.stringify(jsonLog));
  }

  public processDrillbackAndCustomCodeUrl(url: string, name: string): string {
    // if it's a drill back url the logical id is already set in the mingle plugin.
    url = this.lmUrlUtilService.removeParams(url, [ 'csk.LogicalId' ]);

    this.logUnexpectedUrlParams(url, name);
    url = this.removeBadDrillbackParams(url);
    return url;
  }

  /**
   * These params are not allowed to change the mingle plugin context.
   * These params are also not allowed to change the standard client context
   * when using the custom code callback.
   */
  private static badDrillBackParams = [
    'csk.ng3x',
    'csk.ng',
    'csk.cli',
    'csk.3x',
    'csk.developer',
    'csk.showusingxi',
    'csk.hidetitlebar',
    'csk.hidemenubar',
    'csk.displayType',
  ];
  // 'csk.IsoLocale',
  // 'csk.inforThemeName',
  // 'csk.disableuiconfigs',

  // todo talk with Laura Henkey about their needs for csk.IsoLocale.
  // todo: Laura Kenkey suggest talking with Lynn Rivard and Barbie Sokol
  //  about how mingle drillbacks might be using these.
  // 'csk.JobBoard',
  // 'csk.HROrganization',

  private static validDrillBackParams = [
    'csk.gen',
  ];

  /**
   * This method removes "bad" or unsupported mingle and custom code
   * drill back params.
   */
  private removeBadDrillbackParams(url: string): string {
    // --------------------------------------------------------------------------------
    // removing bad params from the drillback url will stop compareSpec from
    // failing when comparing client-build-urls and server-built-urls.
    // it will also stop the csks from getting into the extra spec.contextSessionKeys
    // object, built on the server, and make sure they don't get used in any url.
    // --------------------------------------------------------------------------------
    // todo holding off removing params from mingle drillbacks until we know
    //  that no customer is relying on them.
    // return this.lmUrlUtilService.removeParams(url, LMUrlInfoBuilderService.badDrillBackParams);
    return url;
  }

  private logUnexpectedUrlParams(url: string, type: string): void {
    try {
      const cskParamsNotInContext = this.lmUrlUtilService.getCskParamsNotInContext(url, LMUrlInfoBuilderService.validDrillBackParams);
      if (isEmpty(cskParamsNotInContext)) {
        return;
      }

      const jsonLog = {
        message: `${LMUrlInfoBuilderService.ServerLogVersionId}: ${type} URL ERROR: Found csks not in global ui context`,
        cskParamsNotInContext,
        url,
        type,
        // stack: new Error().stack,
        referrer: document.referrer
      };

      console.error(jsonLog);
      this.lmLoggerService.logToServerConsole(JSON.stringify(jsonLog));
    } catch (error) {
      const jsonLog = {
        message: `${LMUrlInfoBuilderService.ServerLogVersionId}: ${type} URL ERROR: Exception in logUnexpectedUrlParams`,
        stacktrace: `${error.stack}`,
        referrer: document.referrer
      };

      console.error(jsonLog);
      this.lmLoggerService.logToServerConsole(JSON.stringify(jsonLog));
    }
  }

  private getUrlParamsObjectArray(url: string): KeyValueObject[] {
    const paramArray = this.getUrlParamsArray(url);
    if (!paramArray) {
      return;
    }

    const paramObjectArray: KeyValueObject[] = [];
    paramArray.forEach((param: string) => {
      const keyValue = param.split('=');
      paramObjectArray.push({
        key: keyValue[0],
        value: keyValue[1]
      });
    });

    return paramObjectArray;
  }

  private getUrlParamsArray(url: string): string[] | null {
    if (!url) {
      return null;
    }
    url = this.lmUrlUtilService.removeHash(url);

    const paramPos = url.indexOf('?');
    let urlParamStr = '';
    if (paramPos >= 0) {
      urlParamStr = url.substring(paramPos + 1);
    }

    return urlParamStr.split('&');
  }

  /**
   * For the client built urls it uses the urlParams from the browser href.
   * In some cases that will get encoded. For example fc.(SomeField translate).
   * This will be fc.%28SomeField%20translate%29. This causes a mismatch between
   * client and server side urls. It's possible this could be removed once
   * spec url is fully in place.
   */
  private getUrlParams(httpUrl: string) {
    const urlParams = this.lmUrlUtilService.getUrlParams(httpUrl);

    const prefixes = [
      'fc.%28'
    ];

    const newUrlParams: Params = {};

    Object.keys(urlParams).forEach((key: string): void => {
      const keyWithPrefix: string = prefixes.find((prefixValue: string) => key.startsWith(prefixValue));
      const newKey: string = keyWithPrefix ? decodeURIComponent(key) : key;
      newUrlParams[newKey] = urlParams[key];
    });

    return newUrlParams;
  }
}
